import {Palette} from 'hubbl-shared';
import {memo, ReactNode} from 'react';
import styled from 'styled-components';
import {AssetIconChevron} from '../../../assets/icons/icon-chevron/AssetIconChevron';
import {MOBILE_BREAKPOINT} from './constants';

interface CardContainerProps {
  children: ReactNode;
  isPast?: boolean;
  hasActiveEvaluation?: boolean;
  isDeclined?: boolean;
  onClick: () => void;
  variant?: 'default' | 'event' | 'group' | 'connect';
  compressed?: boolean;
}

export const CardContainer = memo(
  ({
    children,
    isPast = false,
    hasActiveEvaluation = false,
    isDeclined = false,
    onClick,
    variant = 'default',
    compressed = false,
  }: CardContainerProps) => {
    return (
      <Container
        onClick={onClick}
        isPast={isPast}
        hasActiveEvaluation={hasActiveEvaluation}
        isDeclined={isDeclined}
        variant={variant}
        compressed={compressed}>
        {children}

        {/* Floating Navigation Chevron */}
        <FloatingChevron>
          <AssetIconChevron size={24} />
        </FloatingChevron>
      </Container>
    );
  },
);

const Container = styled.div<{
  isPast?: boolean;
  hasActiveEvaluation?: boolean;
  isDeclined?: boolean;
  variant?: 'default' | 'event' | 'group' | 'connect';
  compressed?: boolean;
}>`
  position: relative;
  border-radius: 24px;
  overflow: visible;
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  height: ${props => (props.compressed ? '130px' : '200px')};
  display: flex;
  padding: ${props => (props.compressed ? '8px' : '12px')};
  flex-direction: row;
  align-items: stretch;
  background-color: ${Palette.whiteTransparent60};
  box-shadow: ${props =>
    props.compressed
      ? '0 1px 2px rgba(0, 0, 0, 0.05)'
      : '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)'};
  width: 100%;
  min-width: 0;

  /* Event variant with gradient background */
  ${props =>
    props.variant === 'event' &&
    `
    background: linear-gradient(135deg, ${Palette.gradient2Color1} 0%, ${Palette.gradient2Color2} 50%, ${Palette.gradient2Color3} 100%);
  `}

  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow:
      0 8px 25px rgba(0, 0, 0, 0.2),
      0 4px 10px rgba(0, 0, 0, 0.16),
      0 2px 4px rgba(0, 0, 0, 0.12);
  }

  ${props =>
    props.isPast &&
    !props.hasActiveEvaluation &&
    `
    opacity: 0.75;
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    filter: grayscale(0.3);
  `}

  /* Declined state styling */
  ${props =>
    props.isDeclined &&
    !props.isPast &&
    `
    border: 2px solid ${Palette.systemError};
    box-shadow: 
      0 1px 3px rgba(0, 0, 0, 0.05),
      0 1px 2px rgba(0, 0, 0, 0.1),
      0 0 0 1px ${Palette.systemError}40;
  `}

  /* Mobile responsiveness */
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: auto;
    min-height: 160px;
    padding: 16px;
    flex-direction: column;
    gap: 12px;
    margin-right: 16px;
    background-color: ${Palette.white};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const FloatingChevron = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 15;

  display: flex;
  align-items: center;
  justify-content: center;

  pointer-events: none;

  /* Mobile responsiveness */
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    right: 16px;
    top: 16px;
    transform: none;
  }
`;
